/**
 * Homepage Animations
 * Animazioni per i vari elementi della homepage
 */

class HomepageAnimation {
    constructor() {
        this.gridContainer = null;
        this.boxes = [];
        this.animationDelay = 150; // delay tra ogni box (ms)
        this.initialDelay = 300; // delay iniziale prima di far partire l'animazione (ms)
        this.observer = null;

        this.waitForContent();
    }

    addPreventiveCSS() {
        // Aggiunge CSS per nascondere preventivamente qualsiasi .grid-container
        const style = document.createElement('style');
        style.textContent = `
            .grid-container {
                opacity: 0 !important;
                transition: opacity 0.3s ease;
            }
        `;
        document.head.appendChild(style);
        this.preventiveStyle = style;
    }

    waitForContent() {
        // Prima controlla se la griglia esiste già
        this.gridContainer = document.querySelector('.grid-container');
        if (this.gridContainer) {
            this.init();
            return;
        }

        // Se non esiste, usa MutationObserver per aspettare che venga aggiunta
        this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    this.gridContainer = document.querySelector('.grid-container');
                    if (this.gridContainer) {
                        this.observer.disconnect(); // Ferma l'osservazione
                        this.init();
                    }
                }
            });
        });

        // Monitora tutto il body per cambiamenti
        this.observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        // Timeout di sicurezza: se dopo 10 secondi non trova nulla, smetti di cercare
        setTimeout(() => {
            if (this.observer) {
                this.observer.disconnect();
                console.warn('HomepageAnimation: .grid-container non trovato dopo 10 secondi');
            }
        }, 10000);
    }



    init() {
        this.boxes = this.gridContainer.querySelectorAll('.box');
        this.setupInitialState();
        this.startAnimation();
    }

    setupInitialState() {
        // Primo box (LCP, es. mixer) visibile subito per Core Web Vitals; gli altri partono nascosti
        this.boxes.forEach((box, index) => {
            if (index === 0) {
                box.classList.add('box-visible');
            } else {
                box.classList.add('box-hidden');
            }
        });
    }

    startAnimation() {
        // Avvia il video del primo box (LCP) dopo il first paint, così il poster resta LCP
        this.startLcpVideoAfterPaint();
        // Aspetta un po' che la pagina sia completamente caricata
        setTimeout(() => {
            this.animateBoxes();
        }, this.initialDelay);
    }

    startLcpVideoAfterPaint() {
        const firstBox = this.boxes[0];
        if (!firstBox) return;
        const video = firstBox.querySelector('video.bg-video');
        if (!video) return;
        // Dopo il first paint carica e avvia il video (poster già mostrato = LCP ok)
        const startVideo = () => {
            video.preload = 'auto';
            video.load();
            video.play().catch(() => { });
        };
        if (typeof requestIdleCallback !== 'undefined') {
            requestIdleCallback(startVideo, { timeout: 2000 });
        } else {
            setTimeout(startVideo, 500);
        }
    }

    animateBoxes() {
        this.boxes.forEach((box, index) => {
            if (index === 0) return; // già visibile (LCP)
            setTimeout(() => {
                box.classList.remove('box-hidden');
                box.classList.add('box-visible');
            }, index * this.animationDelay);
        });
    }

    // Metodo per far ripartire l'animazione (opzionale)
    restart() {
        // Reset dei box
        this.boxes.forEach(box => {
            box.classList.remove('box-visible');
            box.classList.add('box-hidden');
        });

        setTimeout(() => {
            this.animateBoxes();
        }, 100);
    }

    // Cleanup
    destroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }
}

/**
 * Settori Cascade Animation
 * Animazione a cascata per la sezione settori
 */
class SettoriCascadeAnimation {
    constructor() {
        this.settoriContainer = null;
        this.columns = [];
        this.animationDelay = 150; // delay tra ogni colonna (ms)
        this.observer = null;
        this.observerOptions = {
            threshold: 0.2,
            rootMargin: '0px 0px -50px 0px'
        };

        this.waitForContent();
    }

    waitForContent() {
        // Prima controlla se il container settori esiste già
        this.settoriContainer = document.querySelector('.settori-container');
        if (this.settoriContainer) {
            this.init();
            return;
        }

        // Se non esiste, usa MutationObserver per aspettare che venga aggiunta
        this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    this.settoriContainer = document.querySelector('.settori-container');
                    if (this.settoriContainer) {
                        this.observer.disconnect();
                        this.init();
                    }
                }
            });
        });

        this.observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        // Timeout di sicurezza
        setTimeout(() => {
            if (this.observer) {
                this.observer.disconnect();
            }
        }, 10000);
    }

    init() {
        // Trova tutte le colonne (sia .single che .single--init)
        this.columns = this.settoriContainer.querySelectorAll('.single, .single--init');
        this.setupInitialState();
        this.setupIntersectionObserver();
    }

    setupInitialState() {
        this.columns.forEach((column, index) => {
            // Ogni colonna parte da una posizione sempre più in basso (effetto scala)
            const translateY = (index + 1) * 60; // 60px per ogni step

            column.style.transform = `translateY(${translateY}px)`;
            column.style.opacity = '0';
            column.style.transition = `transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.6s ease`;
            column.classList.add('settori-hidden');
        });
    }

    setupIntersectionObserver() {
        this.intersectionObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && !entry.target.classList.contains('settori-animation-started')) {
                    entry.target.classList.add('settori-animation-started');
                    this.animateColumns();
                }
            });
        }, this.observerOptions);

        this.intersectionObserver.observe(this.settoriContainer);
    }

    animateColumns() {
        this.columns.forEach((column, index) => {
            setTimeout(() => {
                column.style.transform = 'translateY(0)';
                column.style.opacity = '1';
                column.classList.remove('settori-hidden');
                column.classList.add('settori-visible');
            }, index * this.animationDelay);
        });
    }

    // Cleanup
    destroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
        }
    }
}

/**
 * Opposite Slide Animation
 * Animazione per sezioni news/promo: colonna sinistra da una direzione, prodotti da direzione opposta
 */
class OppositeSlidesAnimation {
    constructor() {
        this.sections = [];
        this.animationDelay = 200; // delay iniziale prima delle slide (ms)
        this.slideDelay = 150; // delay tra ogni slide (ms)
        this.observerOptions = {
            threshold: 0.3,
            rootMargin: '0px 0px -50px 0px'
        };

        this.waitForContent();
    }

    waitForContent() {
        // Cerca le sezioni news e promo
        this.sections = document.querySelectorAll('.section-news, .section-promo');
        if (this.sections.length > 0) {
            this.init();
            return;
        }

        // Se non esistono, usa MutationObserver
        this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    this.sections = document.querySelectorAll('.section-news, .section-promo');
                    if (this.sections.length > 0) {
                        this.observer.disconnect();
                        this.init();
                    }
                }
            });
        });

        this.observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        // Timeout di sicurezza
        setTimeout(() => {
            if (this.observer) {
                this.observer.disconnect();
            }
        }, 10000);
    }

    init() {
        this.setupInitialState();
        this.setupIntersectionObserver();
    }

    setupInitialState() {
        this.sections.forEach((section, sectionIndex) => {
            const colInit = section.querySelector('.col-init');
            const slider = section.querySelector('.slider');

            if (colInit && slider) {
                // Alterna la direzione per ogni sezione
                const isEven = sectionIndex % 2 === 0;

                if (isEven) {
                    // Sezione pari: col-init da sopra (-100px), slides da sotto (+100px)
                    this.setupColumnAnimation(colInit, -100, 'slide-from-top');
                    this.setupSlidesAnimation(slider, 100, 'slide-from-bottom');
                } else {
                    // Sezione dispari: col-init da sotto (+100px), slides da sopra (-100px)
                    this.setupColumnAnimation(colInit, 100, 'slide-from-bottom');
                    this.setupSlidesAnimation(slider, -100, 'slide-from-top');
                }
            }
        });
    }

    setupColumnAnimation(column, translateY, className) {
        column.style.transform = `translateY(${translateY}px)`;
        column.style.opacity = '0';
        column.style.transition = 'transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.6s ease';
        column.classList.add('opposite-slide-hidden', className);
    }

    setupSlidesAnimation(sliderContainer, translateY, className) {
        const slides = sliderContainer.querySelectorAll('article.swiper-slide');
        slides.forEach((slide, index) => {
            slide.style.transform = `translateY(${translateY}px)`;
            slide.style.opacity = '0';
            slide.style.transition = 'transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.6s ease';
            slide.classList.add('opposite-slide-hidden', className);
            // Aggiungi un attributo per ricordare l'indice per il delay
            slide.setAttribute('data-slide-index', index);
        });
    }

    setupIntersectionObserver() {
        this.intersectionObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && !entry.target.classList.contains('opposite-animation-started')) {
                    entry.target.classList.add('opposite-animation-started');
                    this.animateSection(entry.target);
                }
            });
        }, this.observerOptions);

        this.sections.forEach(section => {
            this.intersectionObserver.observe(section);
        });
    }

    animateSection(section) {
        const colInit = section.querySelector('.col-init');
        const slider = section.querySelector('.slider');

        if (colInit && slider) {
            // Prima colonna si anima immediatamente
            this.animateColumn(colInit, 0);

            // Le slide si animano una dopo l'altra con delay progressivo
            this.animateSlides(slider, this.animationDelay);
        }
    }

    animateColumn(column, delay) {
        setTimeout(() => {
            column.style.transform = 'translateY(0)';
            column.style.opacity = '1';
            column.classList.remove('opposite-slide-hidden');
            column.classList.add('opposite-slide-visible');
        }, delay);
    }

    animateSlides(sliderContainer, initialDelay) {
        const slides = sliderContainer.querySelectorAll('article.swiper-slide');

        slides.forEach((slide, index) => {
            const totalDelay = initialDelay + (index * this.slideDelay);
            setTimeout(() => {
                slide.style.transform = 'translateY(0)';
                slide.style.opacity = '1';
                slide.classList.remove('opposite-slide-hidden');
                slide.classList.add('opposite-slide-visible');
            }, totalDelay);
        });
    }

    // Cleanup
    destroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
        }
    }
}

/**
 * Trend Cards Animation
 * Animazione per la sezione trend con card che appaiono e immagini che scivolano in posizione
 */
class TrendCardsAnimation {
    constructor() {
        this.trendSection = null;
        this.cards = [];
        this.animationDelay = 150; // delay tra ogni card (ms)
        this.imageDelay = 300; // delay aggiuntivo per l'immagine (ms)
        this.observerOptions = {
            threshold: 0.2,
            rootMargin: '0px 0px -100px 0px'
        };

        this.waitForContent();
    }

    waitForContent() {
        // Cerca la sezione trend
        this.trendSection = document.querySelector('.section-trend');
        if (this.trendSection) {
            this.init();
            return;
        }

        // Se non esiste, usa MutationObserver
        this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    this.trendSection = document.querySelector('.section-trend');
                    if (this.trendSection) {
                        this.observer.disconnect();
                        this.init();
                    }
                }
            });
        });

        this.observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        // Timeout di sicurezza
        setTimeout(() => {
            if (this.observer) {
                this.observer.disconnect();
            }
        }, 10000);
    }

    init() {
        // Trova tutte le card trend (le colonne)
        this.cards = this.trendSection.querySelectorAll('.trend-must .vc_col-sm-3');
        this.setupInitialState();
        this.setupIntersectionObserver();
    }

    setupInitialState() {
        // Aggiungi CSS per le transizioni di animazione
        this.addAnimationCSS();

        this.cards.forEach((card, index) => {
            const image = card.querySelector('.wpb_single_image img');

            // Setup della card - solo trasformazioni, NO transizioni inline
            card.style.transform = 'translateY(40px)';
            card.style.opacity = '0';
            card.classList.add('trend-card-hidden', 'trend-card-animating');

            // Setup dell'immagine - solo trasformazioni, NO transizioni inline
            if (image) {
                image.style.transform = 'translate(30px, -30px)';
                image.style.opacity = '0.7';
                image.classList.add('trend-image-hidden', 'trend-image-animating');
            }
        });
    }

    addAnimationCSS() {
        if (document.querySelector('#trend-animation-styles')) return; // Evita duplicati

        const style = document.createElement('style');
        style.id = 'trend-animation-styles';
        style.textContent = `
            .trend-card-animating {
                transition: transform 0.8s ease-out, opacity 0.6s ease !important;
            }
            .trend-image-animating {
                transition: transform 1s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.8s ease !important;
            }
        `;
        document.head.appendChild(style);
    }

    setupIntersectionObserver() {
        this.intersectionObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && !entry.target.classList.contains('trend-animation-started')) {
                    entry.target.classList.add('trend-animation-started');
                    this.animateCards();
                }
            });
        }, this.observerOptions);

        this.intersectionObserver.observe(this.trendSection);
    }

    animateCards() {
        this.cards.forEach((card, index) => {
            const image = card.querySelector('.wpb_single_image img');

            // Anima la card
            setTimeout(() => {
                card.style.transform = 'translateY(0)';
                card.style.opacity = '1';
                card.classList.remove('trend-card-hidden');
                card.classList.add('trend-card-visible');

                // Rimuovi la classe di transizione dopo l'animazione per non interferire
                setTimeout(() => {
                    card.classList.remove('trend-card-animating');
                }, 800); // Durata della transizione

                // Anima l'immagine con delay aggiuntivo
                if (image) {
                    setTimeout(() => {
                        image.style.transform = 'translate(0, 0)';
                        image.style.opacity = '1';
                        image.classList.remove('trend-image-hidden');
                        image.classList.add('trend-image-visible');

                        // Rimuovi la classe di transizione dopo l'animazione
                        setTimeout(() => {
                            image.classList.remove('trend-image-animating');
                        }, 1000); // Durata della transizione immagine

                    }, this.imageDelay);
                }

            }, index * this.animationDelay);
        });
    }

    // Cleanup
    destroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
        }

        // Rimuovi il CSS aggiunto
        const style = document.querySelector('#trend-animation-styles');
        if (style) {
            style.remove();
        }
    }
}

/**
 * Categories Animation
 * Animazione scale + opacity per la sezione categorie (una dopo l'altra)
 */
class CategoriesCascadeAnimation {
    constructor() {
        this.categoriesContainer = null;
        this.categoryItems = [];
        this.animationDelay = 150; // delay tra ogni categoria (ms)
        this.observer = null;
        this.observerOptions = {
            threshold: 0.2,
            rootMargin: '0px 0px -50px 0px'
        };

        this.waitForContent();
    }

    waitForContent() {
        // Prima controlla se il container categorie esiste già
        this.categoriesContainer = document.querySelector('.section-categories');
        if (this.categoriesContainer) {
            this.init();
            return;
        }

        // Se non esiste, usa MutationObserver per aspettare che venga aggiunta
        this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    this.categoriesContainer = document.querySelector('.section-categories');
                    if (this.categoriesContainer) {
                        this.observer.disconnect();
                        this.init();
                    }
                }
            });
        });

        this.observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        // Timeout di sicurezza
        setTimeout(() => {
            if (this.observer) {
                this.observer.disconnect();
            }
        }, 10000);
    }

    init() {
        // Trova tutte le categorie
        this.categoryItems = this.categoriesContainer.querySelectorAll('.featured_categories_item');
        this.setupInitialState();
        this.setupIntersectionObserver();
    }

    setupInitialState() {
        this.categoryItems.forEach((item, index) => {
            // Animazione semplice: scale + opacity (come la griglia iniziale)
            item.style.transform = 'scale(0.8)';
            item.style.opacity = '0';
            item.style.transition = `transform 0.8s ease-out, opacity 0.6s ease`;
            item.classList.add('categories-hidden');
        });
    }

    setupIntersectionObserver() {
        this.intersectionObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && !entry.target.classList.contains('categories-animation-started')) {
                    entry.target.classList.add('categories-animation-started');
                    this.animateCategories();
                }
            });
        }, this.observerOptions);

        this.intersectionObserver.observe(this.categoriesContainer);
    }

    animateCategories() {
        this.categoryItems.forEach((item, index) => {
            setTimeout(() => {
                item.style.transform = 'scale(1)';
                item.style.opacity = '1';
                item.classList.remove('categories-hidden');
                item.classList.add('categories-visible');
            }, index * this.animationDelay);
        });
    }

    // Cleanup
    destroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
        }
    }
}

/**
 * Newsletter Block Animation
 * Animazione per il blocco newsletter presente in tutte le pagine
 */
class NewsletterAnimation {
    constructor() {
        this.newsletterBlock = null;
        this.isHomepage = this.checkIfHomepage();
        this.canAnimate = !this.isHomepage; // Se non è homepage, può animare subito
        this.observer = null;
        this.observerOptions = {
            threshold: 0.3,
            rootMargin: '0px 0px -100px 0px'
        };

        this.waitForContent();

        // Se è homepage, ascolta quando la griglia finisce
        if (this.isHomepage) {
            this.listenForGridCompletion();
        }
    }

    checkIfHomepage() {
        // Controlla se siamo nella homepage
        return document.body.classList.contains('index') ||
            document.querySelector('.grid-container') !== null ||
            window.location.pathname === '/' ||
            window.location.pathname === '/index.php';
    }

    waitForContent() {
        this.newsletterBlock = document.querySelector('.block_newsletter');
        if (this.newsletterBlock) {
            this.init();
            return;
        }

        // Se non esiste, usa MutationObserver
        this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    this.newsletterBlock = document.querySelector('.block_newsletter');
                    if (this.newsletterBlock) {
                        this.observer.disconnect();
                        this.init();
                    }
                }
            });
        });

        this.observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        // Timeout di sicurezza
        setTimeout(() => {
            if (this.observer) {
                this.observer.disconnect();
            }
        }, 10000);
    }

    init() {
        this.setupInitialState();
        this.setupIntersectionObserver();
    }

    setupInitialState() {
        // Nasconde il newsletter block inizialmente
        this.newsletterBlock.style.transform = 'scale(0.9)';
        this.newsletterBlock.style.opacity = '0';
        this.newsletterBlock.style.transition = 'transform 0.6s ease-out, opacity 0.5s ease';
        this.newsletterBlock.classList.add('newsletter-hidden');
    }

    setupIntersectionObserver() {
        this.intersectionObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting &&
                    !entry.target.classList.contains('newsletter-animation-started') &&
                    this.canAnimate) {
                    entry.target.classList.add('newsletter-animation-started');
                    this.animateNewsletter();
                }
            });
        }, this.observerOptions);

        this.intersectionObserver.observe(this.newsletterBlock);
    }

    listenForGridCompletion() {
        // Ascolta l'evento custom quando la griglia finisce
        document.addEventListener('gridAnimationComplete', () => {
            this.canAnimate = true;
            // Se è già nel viewport e pronto, anima subito
            if (this.newsletterBlock && this.newsletterBlock.classList.contains('newsletter-in-viewport')) {
                this.animateNewsletter();
            }
        });
    }

    animateNewsletter() {
        if (!this.canAnimate) {
            // Se non può ancora animare, segna che è nel viewport per animare dopo
            this.newsletterBlock.classList.add('newsletter-in-viewport');
            return;
        }

        this.newsletterBlock.style.transform = 'scale(1)';
        this.newsletterBlock.style.opacity = '1';
        this.newsletterBlock.classList.remove('newsletter-hidden');
        this.newsletterBlock.classList.add('newsletter-visible');
    }

    // Cleanup
    destroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
        }
    }
}

/**
 * Animation Coordinator
 * Gestisce il coordinamento tra le animazioni
 */
class AnimationCoordinator {
    constructor() {
        this.gridAnimation = null;
        this.isHomepage = this.checkIfHomepage();
    }

    checkIfHomepage() {
        return document.body.classList.contains('index') ||
            document.querySelector('.grid-container') !== null ||
            window.location.pathname === '/' ||
            window.location.pathname === '/index.php';
    }

    initializeAnimations() {
        if (this.isHomepage) {
            // Homepage: inizializza la griglia e poi le altre
            this.gridAnimation = new HomepageAnimation();
            new SettoriCascadeAnimation();
            new OppositeSlidesAnimation();
            new TrendCardsAnimation();
            new CategoriesCascadeAnimation();

            // Calcola quando la griglia sarà finita e lancia l'evento
            this.scheduleGridCompletionEvent();
        }

        // Newsletter in tutte le pagine
        new NewsletterAnimation();
    }

    scheduleGridCompletionEvent() {
        // La griglia ha 300ms di delay iniziale + animazione dei box
        // Assumendo ~6 box con 150ms di delay = 300 + 900 + 800 (transizione) = ~2000ms
        setTimeout(() => {
            document.dispatchEvent(new CustomEvent('gridAnimationComplete'));
        }, 2000);
    }
}

// Inizializza le animazioni quando il DOM è pronto
document.addEventListener('DOMContentLoaded', () => {
    const coordinator = new AnimationCoordinator();
    coordinator.initializeAnimations();
});

// Esporta le classi per uso futuro se necessario
export { HomepageAnimation, SettoriCascadeAnimation, OppositeSlidesAnimation, TrendCardsAnimation, CategoriesCascadeAnimation, NewsletterAnimation, AnimationCoordinator };
